import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { CtaButton } from "@/components/ui/CtaButton";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import {
  BookOpen, Plus, Trash2, Pencil, Sparkles, Atom, Globe2, FlaskConical,
  Calculator, Music, Code2, Brush, Languages, ScrollText, X, ArrowRight,
} from "lucide-react";

const SUBJECT_COLORS = [
  "#FFB400", "#7C3AED", "#EC4899", "#10B981", "#0EA5E9",
  "#F97316", "#EF4444", "#6366F1", "#14B8A6", "#A855F7",
];
const SUBJECT_ICONS = {
  "book-open": BookOpen, atom: Atom, globe: Globe2, flask: FlaskConical,
  calc: Calculator, music: Music, code: Code2, brush: Brush, lang: Languages, scroll: ScrollText,
};

/**
 * /teacher/subjects — top-level Subjects manager.
 *
 * Optional ?grade=<grade_id> filters to that grade.
 * Adding a subject REQUIRES choosing a grade (via searchable dropdown).
 */
export default function Subjects() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const gradeFilter = params.get("grade") || "";
  const [subjects, setSubjects] = useState([]);
  const [grades, setGrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const [s, g] = await Promise.all([api.get("/subjects"), api.get("/grades")]);
      setSubjects(s.data.subjects || []);
      setGrades(g.data.grades || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  const filtered = useMemo(() => gradeFilter ? subjects.filter((s) => s.grade_id === gradeFilter) : subjects, [subjects, gradeFilter]);
  const activeGrade = grades.find((g) => g.id === gradeFilter);

  const onDelete = async (id) => {
    if (!window.confirm("Delete this subject? Linked assets stay — just unlinked.")) return;
    try { await api.delete(`/subjects/${id}`); reload(); toast.success("Subject removed"); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const gradeFilterOptions = [{ value: "", label: "All grades" }, ...grades.map((g) => ({ value: g.id, label: g.name }))];

  return (
    <div className="space-y-6" data-testid="subjects-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-neutral-500">/ subjects{activeGrade ? ` · ${activeGrade.name}` : ""}</div>
          <h1 className="font-display font-bold text-3xl tracking-tighter">Subjects</h1>
          <p className="text-sm text-neutral-500 mt-1">Open a subject to access every AI tool for it — quizzes, lesson plans, recordings, library, and more.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-48">
            <SearchableSelect
              options={gradeFilterOptions}
              value={gradeFilter}
              onChange={(v) => setParams(v ? { grade: v } : {})}
              placeholder="Filter by grade"
              testid="subjects-grade-filter"
              className="rounded-md"
            />
          </div>
          <CtaButton
            onClick={() => grades.length === 0 ? toast.error("Add a grade first under Grades in the sidebar") : setShowAdd(true)}
            variant="primary" size="md" data-testid="add-subject-btn" className="rounded-md"
          >
            <Plus size={14} /> Add subject
          </CtaButton>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-neutral-500">Loading…</div>
      ) : filtered.length === 0 ? (
        grades.length === 0 ? (
          <Empty title="Add a Grade first" sub="Subjects live under a Grade. Head to Grades in the sidebar and add Grade 8, O-Level, Class V — whatever you teach. Then come back here." cta="Go to Grades" to="/teacher/grades" />
        ) : subjects.length === 0 ? (
          <Empty title="No subjects yet" sub="Step 2 of curriculum setup — attach subjects to a grade. Each subject gets its own workspace with every AI tool." cta="Add subject" onCta={() => setShowAdd(true)} />
        ) : (
          <Empty title="No subjects under this grade" sub="Switch the grade filter to see others, or add a subject to this grade." cta="Add subject" onCta={() => setShowAdd(true)} />
        )
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="subjects-grid">
          {filtered.map((s) => {
            const Icon = SUBJECT_ICONS[s.icon] || BookOpen;
            return (
              <div
                key={s.id}
                data-testid={`subject-card-${s.id}`}
                className="group bg-white border border-black/10 rounded-2xl p-5 hover:border-[#0A0C10] transition cursor-pointer relative overflow-hidden"
                onClick={() => navigate(`/teacher/subjects/${s.id}`)}
              >
                <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full opacity-15" style={{ background: s.color }} />
                <div className="flex items-start justify-between relative">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: `${s.color}22`, color: s.color }}>
                    <Icon size={22} />
                  </div>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition" onClick={(e) => e.stopPropagation()}>
                    <button onClick={() => setEditing(s)} data-testid={`subject-edit-${s.id}`} className="p-1.5 text-neutral-500 hover:text-[#0A0C10]"><Pencil size={13} /></button>
                    <button onClick={() => onDelete(s.id)} data-testid={`subject-del-${s.id}`} className="p-1.5 text-red-500 hover:text-red-700"><Trash2 size={13} /></button>
                  </div>
                </div>
                <div className="mt-3 font-display font-bold text-xl tracking-tighter">{s.name}</div>
                <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-neutral-500 mt-1">{s.grade_name || "Unlinked"}</div>
                {s.description && <p className="text-xs text-neutral-500 mt-2 line-clamp-2">{s.description}</p>}
                <div className="mt-4 inline-flex items-center gap-1 text-xs text-[#0A0C10] font-semibold">
                  Open workspace <Sparkles size={12} className="text-[#FFB400]" />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showAdd && <SubjectModal grades={grades} defaultGradeId={gradeFilter} onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); reload(); }} />}
      {editing && <SubjectModal grades={grades} existing={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); }} />}
    </div>
  );
}

function Empty({ title, sub, cta, onCta, to }) {
  return (
    <div className="bg-white border border-dashed themed-border rounded-md p-12 text-center" data-testid="subjects-empty">
      <div className="w-12 h-12 mx-auto rounded-md bg-[#0A0C10] flex items-center justify-center"><BookOpen size={22} className="text-[#FBFAF7]" /></div>
      <h3 className="mt-4 font-display font-bold text-xl tracking-tighter">{title}</h3>
      <p className="text-sm text-neutral-500 mt-1 max-w-md mx-auto">{sub}</p>
      {to ? (
        <Link to={to}><CtaButton variant="primary" size="md" className="mt-5 rounded-md">{cta} <ArrowRight size={14} /></CtaButton></Link>
      ) : (
        <CtaButton onClick={onCta} variant="primary" size="md" className="mt-5 rounded-md"><Plus size={14} /> {cta}</CtaButton>
      )}
    </div>
  );
}

function SubjectModal({ grades, existing = null, defaultGradeId = "", onClose, onSaved }) {
  const [name, setName] = useState(existing?.name || "");
  const [gradeId, setGradeId] = useState(existing?.grade_id || defaultGradeId || (grades[0]?.id || ""));
  const [color, setColor] = useState(existing?.color || SUBJECT_COLORS[0]);
  const [icon, setIcon] = useState(existing?.icon || "book-open");
  const [desc, setDesc] = useState(existing?.description || "");
  const [busy, setBusy] = useState(false);
  const [gradeList, setGradeList] = useState(grades);
  const [showAddGrade, setShowAddGrade] = useState(false);

  useEffect(() => { setGradeList(grades); }, [grades]);

  const submit = async (e) => {
    e.preventDefault();
    if (!gradeId) return toast.error("Pick a grade");
    // Split on comma/newline so a teacher can add multiple subjects at once
    // (e.g. "Physics, Chemistry, Biology" under the same grade). Every subject
    // in the batch shares the same grade, colour, icon and description — the
    // teacher can retouch each one individually later.
    const names = existing
      ? [name.trim()]
      : name.split(/[,\n]/).map((n) => n.trim()).filter(Boolean);
    if (names.length === 0) return toast.error("Subject name required");
    setBusy(true);
    try {
      if (existing) {
        const body = { name: names[0], grade_id: gradeId, color, icon, description: desc.trim() };
        await api.patch(`/subjects/${existing.id}`, body);
        toast.success("Subject updated");
      } else {
        const results = await Promise.allSettled(
          names.map((n) => api.post("/subjects", { name: n, grade_id: gradeId, color, icon, description: desc.trim() }))
        );
        const okCount = results.filter((r) => r.status === "fulfilled").length;
        const failCount = results.length - okCount;
        if (okCount === 0) throw new Error("None saved");
        toast.success(okCount === 1 ? "Subject added" : `Added ${okCount} subjects${failCount ? ` · ${failCount} skipped` : ""}`);
      }
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const onGradeAdded = async () => {
    // Reload grades from the server, close the inline modal, and auto-select
    // the newest grade (server returns them in creation order, latest last).
    setShowAddGrade(false);
    try {
      const { data } = await api.get("/grades");
      const list = data.grades || [];
      setGradeList(list);
      const prevIds = new Set(gradeList.map((g) => g.id));
      const fresh = list.find((g) => !prevIds.has(g.id));
      if (fresh) setGradeId(fresh.id);
    } catch { /* ignore */ }
  };

  const gradeOpts = gradeList.map((g) => ({ value: g.id, label: g.name, hint: g.description || undefined }));

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-md rounded-lg p-6 max-h-[90vh] overflow-y-auto border themed-border shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="subject-modal">
        <div className="flex items-center justify-between mb-5 pb-4 border-b themed-border">
          <div>
            <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-neutral-500">/ subjects</div>
            <h3 className="font-display font-bold text-xl tracking-tighter mt-0.5">{existing ? "Edit subject" : "Add subject"}</h3>
          </div>
          <button onClick={onClose} className="text-neutral-400 hover:text-[#0A0C10] transition-colors"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <Field label={existing ? "Name *" : "Name(s) *"}>
            {existing ? (
              <input className={inp} required value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Mathematics" data-testid="subject-name-input" />
            ) : (
              <>
                <textarea rows={2} className={inp} required value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Mathematics, Physics, Chemistry" data-testid="subject-name-input" />
                <div className="text-[10px] font-mono text-neutral-400 mt-1">Tip · separate multiple subjects with commas or new lines · they all share this grade</div>
              </>
            )}
          </Field>
          <Field label="Grade *">
            <div className="flex items-center gap-2">
              <div className="flex-1 min-w-0">
                <SearchableSelect
                  options={gradeOpts}
                  value={gradeId}
                  onChange={setGradeId}
                  placeholder="Pick a grade"
                  testid="subject-grade-select"
                  className="rounded-md"
                />
              </div>
              <button
                type="button"
                onClick={() => setShowAddGrade(true)}
                data-testid="subject-add-grade-shortcut"
                title="Add a new grade"
                className="shrink-0 w-9 h-9 rounded-md border border-neutral-300 hover:border-[#0A0C10] hover:bg-neutral-50 text-[#0A0C10] inline-flex items-center justify-center transition-colors"
              >
                <Plus size={14} />
              </button>
            </div>
          </Field>
          <Field label="Colour">
            <div className="flex gap-1.5 flex-wrap">
              {SUBJECT_COLORS.map((c) => (
                <button key={c} type="button" onClick={() => setColor(c)} title={c}
                  className={`w-7 h-7 rounded-full ${color === c ? "ring-2 ring-offset-2 ring-[#0A0C10]" : ""}`}
                  style={{ background: c }} data-testid={`color-${c.slice(1)}`} />
              ))}
            </div>
          </Field>
          <Field label="Icon">
            <div className="flex gap-1.5 flex-wrap">
              {Object.entries(SUBJECT_ICONS).map(([k, Icon]) => (
                <button key={k} type="button" onClick={() => setIcon(k)}
                  className={`w-9 h-9 rounded-md border flex items-center justify-center ${icon === k ? "bg-[#0A0C10] text-white border-[#0A0C10]" : "border-neutral-200 hover:border-neutral-400 text-neutral-600"}`}
                  data-testid={`icon-${k}`}>
                  <Icon size={16} />
                </button>
              ))}
            </div>
          </Field>
          <Field label="Description (optional)">
            <textarea rows={3} className={inp} value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Syllabus board, key topics…" data-testid="subject-desc-input" />
          </Field>
          <CtaButton type="submit" disabled={busy} variant="primary" size="lg" className="w-full rounded-md" data-testid="subject-submit">
            <Plus size={14} /> {busy ? "Saving…" : (existing ? "Save changes" : "Create subject(s)")}
          </CtaButton>
        </form>
      </div>

      {showAddGrade && <InlineGradeModal onClose={() => setShowAddGrade(false)} onSaved={onGradeAdded} />}
    </div>
  );
}

/** Minimal in-place "Add grade" modal shown on top of the subject form. Same
 * backend endpoint as the standalone Grades page. Bulk-add supported.
 */
function InlineGradeModal({ onClose, onSaved }) {
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    const names = name.split(/[,\n]/).map((n) => n.trim()).filter(Boolean);
    if (names.length === 0) return toast.error("Grade name required");
    setBusy(true);
    try {
      const results = await Promise.allSettled(
        names.map((n) => api.post("/grades", { name: n, description: desc.trim() }))
      );
      const okCount = results.filter((r) => r.status === "fulfilled").length;
      if (okCount === 0) throw new Error("None saved");
      toast.success(okCount === 1 ? "Grade added" : `Added ${okCount} grades`);
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add grade"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-md rounded-lg p-6 border themed-border shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="inline-grade-modal">
        <div className="flex items-center justify-between mb-4 pb-3 border-b themed-border">
          <div>
            <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-neutral-500">/ grades</div>
            <h3 className="font-display font-bold text-lg tracking-tighter mt-0.5">Add grade</h3>
          </div>
          <button type="button" onClick={onClose} className="text-neutral-400 hover:text-[#0A0C10] transition-colors"><X size={16} /></button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <label className="block">
            <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-neutral-500 mb-1.5">Name(s) *</div>
            <textarea rows={2} className={inp} required value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Grade 9, Grade 10" data-testid="inline-grade-name-input" />
            <div className="text-[10px] font-mono text-neutral-400 mt-1">Tip · commas or new lines to add several at once</div>
          </label>
          <label className="block">
            <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-neutral-500 mb-1.5">Description (optional)</div>
            <textarea rows={2} className={inp} value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Stage, board, age group…" data-testid="inline-grade-desc-input" />
          </label>
          <CtaButton type="submit" disabled={busy} variant="primary" size="lg" className="w-full rounded-md" data-testid="inline-grade-submit">
            <Plus size={14} /> {busy ? "Saving…" : "Create grade(s)"}
          </CtaButton>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-neutral-500 mb-1">{label}</div>
      {children}
    </label>
  );
}

const inp = "w-full bg-white text-sm border border-neutral-200 rounded-md px-3 py-2 outline-none focus:border-[#0A0C10]";
